import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../../environment";

const BASE_URL = `${API_BASE_PATH}/Task`;

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

export interface Task {
  Assign_to: string;
  ticket_id: string;
  created_by: string;
  task_name: string;
  due_date: string;
  team_member: string[] | null;
  tags: string[] | null;
  priority: string;
  status: string;
  view: number;
  descriptions: string | null;
  upload_file?: File | null;
  start_date: string | null;
  end_date: string | null;
  total_hours?: number;
  estimated_hours: number;
  task_start: string | null;
  task_end: string | null;
  reason: string;
  employee: Employee;
  created_by_employee: Employee;
  escalate_to_employee: Employee | null;
  task_history: TaskHistoryEntry[];
  ticket: Ticket;
  created_at: string;
}

export interface TaskHistoryEntry {
  id: number;
  task_id: string;
  status: string;
  total_hours: string | null;
  reason: string | null;
  entered_at: string;
  exited_at: string | null;
  employee_id: string;
  employee_name: string;
}

export interface Employee {
  employee_id: string;
  firstname: string;
  lastname: string;
  username: string;
  email: string;
}

export interface Ticket {
  enquiry_id: string;
  ticket_title: string;
  description: string;
  Status: string;
  priority: string;
  assigned_to: string;
  created_by: string;
  ticket_id: string;
  enquiry: Enquiry;
  created_at: string;
  creator_employee: Employee;
  assigned_employee: Employee;
}

export interface Category {
  cate_id: number;
  cate_name: string;
}

export interface Enquiry {
  company_name: string;
  company_license: string;
  email: string;
  product_id: number;
  module_id: number;
  taken_by: string;
  requirement: string;
  upload_file: any;
  status: string;
  category: Category;
}

export interface TaskPayload {
  task_name: string;
  ticket_id: string;
  Assign_to: string;
  due_date: string;
  tags?: string[] | null;
  priority: string;
  status: string;
  view?: number | null;
  descriptions?: string | null;
  upload_file?: File | null;
  start_date?: string | null;
  end_date?: string | null;
  estimated_hours?: number | null;
  created_by?: string;
}

// POST new task
export const createTask = async (
  _token: string | null,
  taskData: FormData
): Promise<any> => {
  try {
    const response = await axios.post(`${BASE_URL}/`, taskData, {
      headers: {
        accept: "application/json",
        "Content-Type": "multipart/form-data",
        authorization: "Bearer " + _token,
      },
    });

    toast.success("Task created successfully");
    return response.data;
  } catch (error: any) {
    const msg = extractErrorMessage(error);
    toast.error(msg);
    throw error;
  }
};

export const getAllTasks = async (_token: string | null): Promise<Task[]> => {
  try {
    const response = await axios.get(`${BASE_URL}/`, {
      headers: {
        accept: "application/json",
        authorization: "Bearer " + _token,
      },
    });
    const tasksWithReason = response.data.map((task: any) => {
      const latestReason =
        task.task_history
          ?.slice()
          .reverse()
          .find((entry: any) => entry.reason)?.reason || "";
      const creatorName = task.created_by_employee
        ? `${task.created_by_employee.firstname} ${task.created_by_employee.lastname}`
        : "";

      const assignedName = task.employee
        ? `${task.employee.firstname} ${task.employee.lastname}`
        : "";

      return {
        ...task,
        reason: latestReason,
        creator_employee: creatorName,
        assigned_employee: assignedName,
      };
    });
    return tasksWithReason;
  } catch (error: any) {
    const msg = extractErrorMessage(error);
    toast.error(msg);
    throw error;
  }
};

// PUT task status update
export const updateTaskStatus = async (
  _token: string | null,
  task_id: string,
  payload: {
    ticket_id: string;
    status: string;
    reason?: string | null;
    priority: string;
    employee_id: string;
    employee_name: string;
    escalate_to?: string | null;
  }
): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append("ticket_id", payload.ticket_id);
    formData.append("status", payload.status);
    formData.append("priority", payload.priority);
    formData.append("employee_id", payload.employee_id);
    formData.append("employee_name", payload.employee_name);
    if (payload.reason) {
      formData.append("reason", payload.reason);
    }
    if (payload.escalate_to) {
      formData.append("escalate_to", payload.escalate_to);
    }

    const response = await axios.put(`${BASE_URL}/${task_id}`, payload, {
      headers: {
        "Content-Type": "multipart/form-data",
        authorization: "Bearer " + _token,
      },
    });
    toast.success("Task status updated successfully");
    return response.data;
  } catch (error: any) {
    const msg = extractErrorMessage(error);
    toast.error(msg);
    throw error;
  }
};
